import { AlertTriangle, FileText, Lightbulb, User, Users } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { blockedAction, type BlockedActionItem } from "./blockedAction";

/**
 * The API phrases every in-use refusal the same way:
 *   "This <subject> cannot be deleted because it is used by <n> active
 *    <noun>: <name>, <name>, <name> and <k> more. <hint>"
 * (the adjective varies — "active contracts", "in-progress contracts")
 * Pull that apart so the dialog can lay the pieces out — summary, a list of
 * the records in the way, the hint — instead of one dense paragraph. Any
 * message that does not fit the shape is shown verbatim.
 */
interface ParsedRefusal {
  summary: string;
  /** What the blocking records are — "contract", "staff member", "party". */
  noun: string;
  items: string[];
  more: number;
  hint: string;
}

function splitOutsideParens(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of text) {
    if (ch === "(") depth++;
    if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      parts.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());
  return parts.filter(Boolean);
}

function parseRefusal(message: string): ParsedRefusal | null {
  const m = message.match(
    /^(.*?\bused by \d+ ([^:.]+?))(?::\s*(.+?))?(?:\s+and (\d+) more)?\.\s*(.*)$/s,
  );
  if (!m) return null;
  return {
    summary: m[1].trim() + ".",
    noun: m[2].trim().toLowerCase(),
    items: m[3] ? splitOutsideParens(m[3]) : [],
    more: m[4] ? Number(m[4]) : 0,
    hint: (m[5] || "").trim(),
  };
}

/** Row icon for the kind of record in the way: people, parties or documents. */
function iconFor(noun: string) {
  if (/staff|member|user/.test(noun)) return User;
  if (/part/.test(noun)) return Users;
  return FileText;
}

/** "LAWS-PAR-202609-0032 (QA Workflow Test Title)" → number + title. */
function splitItem(item: string): { primary: string; secondary?: string } {
  const m = item.match(/^(.+?)\s+\((.+)\)$/);
  return m ? { primary: m[1], secondary: m[2] } : { primary: item };
}

/**
 * Renders whatever blockedAction.show(...) last asked for. Styled like
 * ConfirmModal's warning variant so a refusal reads as part of the same
 * delete flow, but with a single Close button — there is nothing to confirm.
 */
export function BlockedActionModalHost() {
  const [item, setItem] = useState<BlockedActionItem | null>(null);
  const { t } = useTranslation(["common"]);

  useEffect(() => blockedAction.subscribe(setItem), []);

  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = "hidden";
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") blockedAction.dismiss();
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handler);
    };
  }, [item]);

  const parsed = useMemo(
    () => (item ? parseRefusal(item.message) : null),
    [item],
  );

  if (typeof window === "undefined") return null;

  const title =
    item?.title ||
    t("common:blockedAction.title", { defaultValue: "Cannot delete" });

  return createPortal(
    <AnimatePresence>
      {item && (
        <m.div
          className="fixed inset-0 z-[100001] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <m.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => blockedAction.dismiss()}
          />

          <m.div
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 12, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="
              relative z-10
              mx-4 w-full max-w-lg
              overflow-hidden rounded-2xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--card))]
              shadow-2xl
            "
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="blocked-action-title"
          >
            {/* HEADER — the coloured band is the app's modal convention;
                amber marks "stopped, nothing lost" as opposed to the red
                the delete confirmation itself wears. */}
            <div className="flex items-center gap-3 bg-yellow-700 px-6 py-4 text-white">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <AlertTriangle size={18} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2
                  id="blocked-action-title"
                  className="text-lg font-semibold leading-snug"
                >
                  {title}
                </h2>
                <p className="text-xs text-white/85">
                  {t("common:blockedAction.subtitle", {
                    defaultValue: "Nothing was changed",
                  })}
                </p>
              </div>
            </div>

            {/* BODY */}
            <div className="space-y-4 px-6 py-5">
              {parsed ? (
                <>
                  <p className="text-sm leading-relaxed text-[rgb(var(--text-primary))]">
                    {parsed.summary}
                  </p>

                  {parsed.items.length > 0 && (
                    <ul className="divide-y divide-[rgb(var(--border))] overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]">
                      {parsed.items.map((raw) => {
                        const { primary, secondary } = splitItem(raw);
                        const ItemIcon = iconFor(parsed.noun);
                        return (
                          <li
                            key={raw}
                            className="flex items-center gap-3 px-4 py-2.5"
                          >
                            <ItemIcon
                              size={16}
                              aria-hidden="true"
                              className="shrink-0 text-[rgb(var(--text-accent))]"
                            />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-[rgb(var(--text-primary))]">
                                {primary}
                              </div>
                              {secondary && (
                                <div className="truncate text-xs text-[rgb(var(--text-muted))]">
                                  {secondary}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                      {parsed.more > 0 && (
                        <li className="px-4 py-2 text-xs text-[rgb(var(--text-muted))]">
                          {t("common:blockedAction.more", {
                            defaultValue: "+ {{count}} more",
                            count: parsed.more,
                          })}
                        </li>
                      )}
                    </ul>
                  )}

                  {parsed.hint && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-[rgb(var(--text-secondary))]">
                      <Lightbulb
                        size={16}
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-yellow-700"
                      />
                      <span>{parsed.hint}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="whitespace-pre-line text-sm leading-relaxed text-[rgb(var(--text-secondary))]">
                  {item.message}
                </p>
              )}
            </div>

            {/* FOOTER */}
            <div className="flex justify-end border-t border-[rgb(var(--border))] bg-[rgb(var(--card-muted))] px-6 py-4">
              <button
                autoFocus
                onClick={() => blockedAction.dismiss()}
                className="
                  rounded-lg px-5 py-2 text-sm font-medium text-[rgb(var(--accent-ink))]
                  bg-[rgb(var(--accent-fill))] hover:opacity-90
                  transition-opacity
                  focus:outline-none focus-visible:ring-2
                  focus-visible:ring-[rgb(var(--text-accent))]
                "
              >
                {t("common:blockedAction.close", { defaultValue: "Close" })}
              </button>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
